import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as personalSpaceApi from '@/features/personal-space/api';
import type { PersonalSpaceView } from '@/features/personal-space/types';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { ApiError } from '@/lib/api/client';

/**
 * Personal Memorization Space (ADR-031).
 *
 * Deliberately outside `TENANT_QUERY_PREFIX`: a personal space is an
 * account-level fact ("which of my organizations, if any, is my own"), not
 * data belonging to whichever organization happens to be active, so
 * switching organizations must neither change nor discard this answer.
 * The same reasoning `features/auth/hooks.ts` applies to the account's own
 * device list.
 */
export const PERSONAL_SPACE_QUERY_KEY = ['personal-space'] as const;

export function usePersonalSpace(
  enabled = true,
): UseQueryResult<PersonalSpaceView | null, ApiError> {
  return useQuery({
    queryKey: PERSONAL_SPACE_QUERY_KEY,
    queryFn: () => personalSpaceApi.getPersonalSpace(),
    enabled,
  });
}

/**
 * True when the Active Organization *is* the caller's own personal space.
 *
 * Answers the one question three separate surfaces need before they can
 * decide what to render: the dashboard (which cards apply), the personal
 * space card (whether it belongs on screen at all), and the app shell
 * (which navigation entries mean anything in a tenant of one). Each had
 * written the same four-way comparison out longhand, including the two
 * null checks `usePersonalSpace`'s loading state forces.
 *
 * Returns false while the query is still resolving, so navigation and
 * cards settle into the pondok shape and then narrow - never the reverse,
 * which would flash personal-space chrome at a pondok member.
 */
export function useIsPersonalSpace(): boolean {
  const { activeOrganizationId } = useActiveOrganization();
  const { data: personalSpace } = usePersonalSpace();

  return (
    personalSpace !== null &&
    personalSpace !== undefined &&
    activeOrganizationId !== null &&
    personalSpace.organizationId === activeOrganizationId
  );
}

/**
 * Provisions the caller's personal space. The caller is responsible for
 * refreshing the auth session afterwards (`refreshUser`) before selecting
 * the new organization - the membership this creates must be re-resolved
 * from `/auth/me`, never guessed at from this response (ADR-026 Addendum,
 * AO-13; the same discipline the invitation-acceptance page follows).
 */
export function useCreatePersonalSpace(): UseMutationResult<PersonalSpaceView, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => personalSpaceApi.createPersonalSpace(),
    onSuccess: (space) => {
      queryClient.setQueryData<PersonalSpaceView | null>(PERSONAL_SPACE_QUERY_KEY, space);
    },
  });
}
